import React, { useState } from 'react';
import PropTypes from 'prop-types';

const ShoppingListItemForm = ({ item, pantryItems = [], onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: item?.name || '',
    quantity: item?.quantity ?? '',
    unit: item?.unit || '',
    category: item?.category || '',
    price: item?.price ?? '',
    pantryItemId: item?.pantryItemId || '',
  });

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    if (name === 'pantryItemId' && value) {
      const selectedPantryItem = pantryItems.find(item => item.id === value);
      if (selectedPantryItem) {
        setFormData(prev => ({
          ...prev,
          name: selectedPantryItem.name,
          unit: selectedPantryItem.unit || '',
          category: selectedPantryItem.category || '',
          price: selectedPantryItem.price ?? '',
          pantryItemId: value,
        }));
        return;
      }
    }
    if (type === 'number') {
      // Allow empty string for easier editing
      setFormData(prev => ({
        ...prev,
        [name]: value === '' ? '' : (parseFloat(value) || ''),
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleBlur = (e) => {
    const { name, type } = e.target;
    if (type === 'number') {
      const currentValue = formData[name];
      if (currentValue === '') {
        if (name === 'quantity') {
          setFormData(prev => ({ ...prev, [name]: 1 }));
        } else {
          setFormData(prev => ({ ...prev, [name]: 0 }));
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      quantity: formData.quantity === '' ? 1 : (parseFloat(formData.quantity) || 1),
      price: formData.price === '' ? 0 : (parseFloat(formData.price) || 0),
    };
    onSave(submitData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-white rounded-lg shadow">
      {pantryItems.length > 0 && (
        <div>
          <label htmlFor="pantryItemId" className="block text-sm font-medium text-gray-700 mb-1">
            Select from Pantry
          </label>
          <select
            id="pantryItemId"
            name="pantryItemId"
            value={formData.pantryItemId}
            onChange={handleChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="">-- Add New Item --</option>
            {pantryItems.map(item => (
              <option key={item.id} value={item.id}>
                {item.name} ({item.quantity} {item.unit})
              </option>
            ))}
          </select>
        </div>
      )}

      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Item Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
            Quantity *
          </label>
          <input
            type="number"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            onBlur={handleBlur}
            min="1"
            step="0.01"
            required
            placeholder="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>

        <div>
          <label htmlFor="unit" className="block text-sm font-medium text-gray-700 mb-1">
            Unit
          </label>
          <input
            type="text"
            id="unit"
            name="unit"
            value={formData.unit}
            onChange={handleChange}
            placeholder="e.g., lbs, oz, count"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
      </div>

      <div>
        <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
          Category
        </label>
        <input
          type="text"
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          placeholder="e.g., Produce, Dairy, Meat"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
      </div>

      <div>
        <label htmlFor="price" className="block text-sm font-medium text-gray-700 mb-1">
          Price (per unit)
        </label>
          <input
            type="number"
            id="price"
            name="price"
            value={formData.price}
            onChange={handleChange}
            onBlur={handleBlur}
            min="0"
            step="0.01"
            placeholder="0.00"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
      </div>

      <div className="flex space-x-3 pt-2">
        <button
          type="submit"
          className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {item ? 'Update' : 'Add'} to List
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

ShoppingListItemForm.propTypes = {
  item: PropTypes.object,
  pantryItems: PropTypes.array,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

export default ShoppingListItemForm;

